import {
  Field,
  Select,
  SelectOption,
  SelectSizeVariant,
} from '@payaca/ambrosia';
import { FieldInputSwitch } from '@payaca/custom-fields-react';
import {
  CustomFieldConditionOperator,
  operatorCarriesValue,
  OPERATORS_BY_FIELD_TYPE,
} from '@payaca/gql-helpers';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useMemo } from 'react';

import { useGetMyAccountUsersExcludedDeactivated } from '@/api/queries/me/useGetMyAccountUsersExcludedDeactivated';
import { useTranslation } from '@/i18n';

import { ExtraPickerProps } from '../../../types/complexConditionDef';
import CustomFieldPicker from './CustomFieldPicker';
import { useCustomFieldPickerNodes } from './customFieldConditionPickerNodes';

export type CustomFieldConditionExtra = {
  fieldPath: string;
  /** `''` while the field is unselected (the schema rejects this state). */
  operator: '' | CustomFieldConditionOperator;
  comparisonValue?: unknown;
};

const CustomFieldConditionExtraPicker: FC<
  ExtraPickerProps<CustomFieldConditionExtra>
> = (props) => {
  const { value, onChange, errors } = props;
  const t = useTranslation();
  const { nodesByPath } = useCustomFieldPickerNodes();
  const { data: queryUsers } = useGetMyAccountUsersExcludedDeactivated();

  const usersForFieldInput = useMemo(
    () => (queryUsers ?? []).map((u) => ({ ...u, id: u.publicId })),
    [queryUsers]
  );

  const selectedNode = useMemo(
    () => (value.fieldPath ? nodesByPath.get(value.fieldPath) : undefined),
    [nodesByPath, value.fieldPath]
  );

  const operatorOptions = useMemo<SelectOption<string>[]>(() => {
    if (!selectedNode) return [];
    const applicableOperators =
      OPERATORS_BY_FIELD_TYPE[selectedNode.definition.type] ?? [];
    return applicableOperators.map((operator) => ({
      value: operator,
      label: t(
        `pipelineGuards.conditions.CUSTOM_FIELD_CONDITION.operator.${operator}`
      ),
    }));
  }, [selectedNode, t]);

  return (
    <div className="mt-2 flex flex-col gap-2">
      <Field.Legacy
        validationState={
          errors.fieldPath
            ? { isValid: false, validationMessages: [errors.fieldPath] }
            : undefined
        }
      >
        <div className="relative flex gap-2 items-center">
          <UntitledIcon
            name="corner-down-right.3"
            className="size-5 shrink-0"
          />
          <Field.Label className="sr-only">
            {t('pipelineGuards.conditions.CUSTOM_FIELD_CONDITION.field.label')}
          </Field.Label>
          <div className="flex-1 min-w-0">
            <CustomFieldPicker
              value={value.fieldPath}
              onChange={(node) => {
                // Changing the field resets operator + comparisonValue —
                // the new field's type may have a disjoint operator set
                // and a value typed in for the previous field rarely
                // makes sense for the new one.
                onChange({
                  fieldPath: node.fieldPath,
                  operator: '',
                  comparisonValue: undefined,
                });
              }}
            />
          </div>
        </div>
      </Field.Legacy>

      {selectedNode && (
        <Field.Legacy
          validationState={
            errors.operator
              ? { isValid: false, validationMessages: [errors.operator] }
              : undefined
          }
        >
          <div className="relative flex gap-2 items-center">
            <UntitledIcon
              name="corner-down-right.3"
              className="size-5 shrink-0 invisible"
            />
            <Field.Label className="sr-only">
              {t(
                'pipelineGuards.conditions.CUSTOM_FIELD_CONDITION.operator.label'
              )}
            </Field.Label>
            <Select
              sizeVariant={SelectSizeVariant.SM}
              options={operatorOptions}
              multiple={false}
              value={value.operator}
              placeholder={t(
                'pipelineGuards.conditions.CUSTOM_FIELD_CONDITION.operator.placeholder'
              )}
              onChange={(nextOperator) => {
                // Changing the operator resets comparisonValue. For
                // shipped operators only equality variants carry a
                // value, so the practical effect is "going from
                // value-bearing to valueless" — but the reset also
                // future-proofs against operators like `startsWith`
                // whose value shape would differ.
                onChange({
                  fieldPath: value.fieldPath,
                  operator: nextOperator as CustomFieldConditionOperator,
                  comparisonValue: undefined,
                });
              }}
            />
          </div>
        </Field.Legacy>
      )}

      {selectedNode &&
        value.operator !== '' &&
        operatorCarriesValue(value.operator) && (
          <Field.Legacy
            validationState={
              errors.comparisonValue
                ? {
                    isValid: false,
                    validationMessages: [errors.comparisonValue],
                  }
                : undefined
            }
          >
            <div className="relative flex gap-2 items-start">
              <UntitledIcon
                name="corner-down-right.3"
                className="size-5 shrink-0 invisible"
              />
              <div className="flex-1 min-w-0">
                <Field.Label className="sr-only">
                  {t(
                    'pipelineGuards.conditions.CUSTOM_FIELD_CONDITION.comparisonValue.label'
                  )}
                </Field.Label>
                <FieldInputSwitch
                  definition={selectedNode.definition}
                  value={value.comparisonValue}
                  onChange={(nextValue: unknown) => {
                    onChange({
                      fieldPath: value.fieldPath,
                      operator: value.operator,
                      comparisonValue: nextValue,
                    });
                  }}
                  users={usersForFieldInput}
                />
              </div>
            </div>
          </Field.Legacy>
        )}
    </div>
  );
};

export default CustomFieldConditionExtraPicker;
